import { ChangePasswordBindingModel } from "@switcherstudio/switcher-api-client";
import { Button } from "components/buttons/Button";
import { TextInput } from "components/inputs/text-input/TextInput";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { Errors, PasswordValidation, useForm } from "hooks/useForm";
import { PasswordCriteria } from "components/password-criteria/PasswordCriteria";

interface ChangePasswordFormProps {
    /** Handler for password update form submission. */
    onSubmit: (payload: ChangePasswordBindingModel) => Promise<void>;
    loading?: boolean;
}

export const ChangePasswordForm = ({
    onSubmit,
    loading = false
}: ChangePasswordFormProps) => {
    const { t } = useTranslation();

    const validatePasswordForm = useCallback(
        (values: ChangePasswordBindingModel, pvm?: PasswordValidation) => {
            let errors: Errors = {};

            if (!values.OldPassword) {
                errors.OldPassword = t("errors:password-error");
            }

            if (!values.NewPassword) {
                errors.NewPassword = t("errors:password-required-error");
            } else if (pvm && !pvm.hasMinLength) {
                errors.NewPassword = t("errors:password-length-error");
            } else if (pvm && !pvm.hasLower) {
                errors.NewPassword = t("errors:password-lower-error");
            } else if (pvm && !pvm.hasUpper) {
                errors.NewPassword = t("errors:password-upper-error");
            } else if (pvm && !pvm.hasDigit) {
                errors.NewPassword = t("errors:password-digit-error");
            } else if (pvm && !pvm.hasSpecialChar) {
                errors.NewPassword = t("errors:password-special-char-error");
            }

            if (!values.ConfirmPassword) {
                errors.ConfirmPassword = t("errors:password-confirm-error");
            } else if (values.NewPassword !== values.ConfirmPassword) {
                errors.ConfirmPassword = t("errors:password-match-error");
            }

            return errors;
        },
        [t]
    );

    const {
        values,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
        passwordValidMap
    } = useForm<ChangePasswordBindingModel>(
        {
            OldPassword: "",
            NewPassword: "",
            ConfirmPassword: ""
        },
        onSubmit,
        validatePasswordForm,
        true
    );

    const disableChangePassword = useMemo(() => {
        const hasAllValues =
            !!values.OldPassword?.trim() &&
            !!values.NewPassword?.trim() &&
            !!values.ConfirmPassword?.trim();

        if (!hasAllValues) return true;

        const validationErrors = validatePasswordForm(values, passwordValidMap);
        return Object.keys(validationErrors).length > 0;
    }, [values, passwordValidMap, validatePasswordForm]);

    return (
        <div className={styles["password-form"]}>
            <div className="row">
                <div className="col-12">
                    <TextInput
                        id="OldPassword"
                        type="password"
                        label={t("profile:old-password")}
                        value={values?.OldPassword}
                        error={errors?.OldPassword}
                        onChange={handleChange}
                        autocomplete="new-password"
                    />
                    <TextInput
                        id="NewPassword"
                        type="password"
                        label={t("profile:new-password")}
                        value={values?.NewPassword}
                        error={errors?.NewPassword}
                        onChange={handleChange}
                        autocomplete="new-password"
                    />
                    <TextInput
                        id="ConfirmPassword"
                        type="password"
                        label={t("profile:confirm-new-password")}
                        value={values?.ConfirmPassword}
                        error={errors?.ConfirmPassword}
                        onChange={handleChange}
                        autocomplete="new-password"
                    />
                    <PasswordCriteria passwordValidMap={passwordValidMap} />
                    <div className={`${styles["button-container"]}`}>
                        <Button
                            onClick={() => handleSubmit()}
                            disabled={
                                loading || isSubmitting || disableChangePassword
                            }
                            type="primary"
                        >
                            {t("buttons:change-password").toUpperCase()}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
